// app/components/home/PoonPhol.tsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import { Button, Box, Grid } from "@mui/material";
import { useLocale, useTranslations } from "next-intl";
import { renderWithLineBreaks } from "@/app/utils/renderWithLineBreaks";

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string | null;
};

export default function PoonPhol() {
  const t = useTranslations("Home");
  const locale = useLocale();
  const pagePath = "/"; // หน้า Home

  const poonpholFixedBgrRef = useRef<HTMLDivElement>(null);

  // ---- default text จาก i18n (ดีต่อ SEO / SSR) ----
  const defaultTitle = t("PoonPhol.title");
  const defaultSubtitle = t("PoonPhol.subtitle");
  const defaultContent1 = t("PoonPhol.content1");
  const defaultContent2 = t("PoonPhol.content2");

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [title, setTitle] = useState(defaultTitle);
  const [subtitle, setSubtitle] = useState(defaultSubtitle);
  const [content1, setContent1] = useState(defaultContent1);
  const [content2, setContent2] = useState(defaultContent2);

  // เมื่อ locale เปลี่ยน → reset กลับค่า default ก่อน
  useEffect(() => {
    setTitle(defaultTitle);
    setSubtitle(defaultSubtitle);
    setContent1(defaultContent1);
    setContent2(defaultContent2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

 //
  useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;

        const map: Record<string, string> = {};

        list.forEach((e) => {
          let key = e.cmsKey ?? undefined;

          // fallback: parse จาก selector ถ้าเป็น row เก่า ๆ
          if (!key) {
            const match = e.selector.match(/data-cms-key="([^"]+)"/);
            if (match) {
              key = match[1];
            }
          }

          if (key) {
            map[key] = e.value;
          }
        });

        // override เฉพาะ key ที่มีใน inline
        if (map["home.poonphol.title"]) {
          setTitle(map["home.poonphol.title"]);
        }
        if (map["home.poonphol.subtitle"]) {
          setSubtitle(map["home.poonphol.subtitle"]);
        }
        if (map["home.poonphol.content1"]) {
          setContent1(map["home.poonphol.content1"]);
        }
        if (map["home.poonphol.content2"]) {
          setContent2(map["home.poonphol.content2"]);
        }
      } catch (err) {
        console.warn("[PoonPhol] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  // ---- parallax / fixed background เดิม ----
  useEffect(() => {
    const handleScroll = () => {
      if (!poonpholFixedBgrRef.current) return;
      const introTop =
        poonpholFixedBgrRef.current.getBoundingClientRect().top;
      if (introTop <= 0) {
        poonpholFixedBgrRef.current.classList.add("bg-fixed");
      } else {
        poonpholFixedBgrRef.current.classList.remove("bg-fixed");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Box className="poonphol-wrap" ref={poonpholFixedBgrRef}>
      <Box className="poonphol-content" sx={{
        py: { xs: 5, md: 10, lg: 0 }
      }}>
        <Container className="container">
          <Grid container>
            <Grid
              size={{ xs: 12, md: 8, lg: 7 }}
              offset={{ xs: 0, md: 4, lg: 5 }}
              data-aos="fade-up"
            >
              <Typography
                variant="h2"
                data-heading-tag="H2"
                data-cms-key="home.poonphol.title"
                sx={{
                  mb: 2,
                  whiteSpace: { xs: "normal", lg: "pre-line" }
                }}>
                {renderWithLineBreaks(title)}
              </Typography>

              <Typography
                variant="h3"
                data-heading-tag="H3"
                data-cms-key="home.poonphol.subtitle"
                sx={{
                  mb: 3
                }}
              >
                {renderWithLineBreaks(subtitle)}
              </Typography>

              <Typography
                variant="body1"
                sx={{ textIndent: 2, mb: 2 }}
                data-cms-key="home.poonphol.content1"
              >
                {renderWithLineBreaks(content1)}
              </Typography>

              <Typography
                variant="body1"
                sx={{ textIndent: 2, mb: 3 }}
                data-cms-key="home.poonphol.content2"
              >
                {renderWithLineBreaks(content2)}
              </Typography>

              <Button
                variant="contained"
                className="readmore-btn"
                href="https://www.poonphol.com/th/home"
                target="_blank"
              >
                {t("viewMore")}
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}